/**
 * Every string the canvas iterates over, transcribed verbatim.
 *
 * Only the four `<sc-for>` lists live here. Copy that appears exactly once —
 * headings, ledes, the dashboard's sample figures — stays inline in the
 * section that renders it, because lifting a single-use string into a data
 * file buys nothing and costs a lookup when someone is comparing this against
 * the canvas side by side.
 *
 * The typographic characters matter and are intentional: curly apostrophes in
 * the FAQ copy, an em dash in the trust bodies, a middle dot in the hero rail.
 * Replacing them with ASCII would be a visible change, not a cleanup.
 */

export interface Agent {
  id: string;
  glyph: string;
  name: string;
  desc: string;
  intents: string[];
}

/** Order is the canvas's. Marketing sits last and is the default selection. */
export const AGENTS: Agent[] = [
  {
    id: "finance",
    glyph: "$",
    name: "Finance Agent",
    desc: "Close the books, run payroll, and keep cash flow visible.",
    intents: [
      "Run payroll for March",
      "Flag invoices overdue past 30 days",
      "Forecast Q3 cash position",
    ],
  },
  {
    id: "hr",
    glyph: "\u2691",
    name: "HR Agent",
    desc: "Hiring, onboarding, and people ops without the busywork.",
    intents: [
      "Draft the Q3 hiring plan",
      "Onboard the two engineers starting Monday",
      "Who is over capacity this sprint?",
    ],
  },
  {
    id: "ops",
    glyph: "\u25A6",
    name: "Ops Agent",
    desc: "Keeps projects, tasks, and handoffs moving on schedule.",
    intents: [
      "Rebalance tasks across the sales team",
      "What is blocking the website launch?",
      "Schedule the weekly ops review",
    ],
  },
  {
    id: "knowledge",
    glyph: "\u25EB",
    name: "Knowledge Agent",
    desc: "Answers from your documents, decisions, and history.",
    intents: [
      "Summarize last quarter\u2019s board notes",
      "What did we decide on pricing?",
      "Find the signed MSA with Acme",
    ],
  },
  {
    id: "inventory",
    glyph: "\u25CD",
    name: "Inventory Agent",
    desc: "Stock levels, reordering, and supplier follow-through.",
    intents: [
      "Which SKUs run out this month?",
      "Draft a reorder for low stock",
      "Compare supplier lead times",
    ],
  },
  {
    id: "marketing",
    glyph: "\u2709",
    name: "Marketing Agent",
    desc: "Campaign planning, lead scoring, and ROI attribution.",
    intents: [
      "Score inbound leads from last week",
      "Build the Q3 campaign calendar",
      "Which channel drove pipeline?",
    ],
  },
];

/** The canvas falls back to index 5 — Marketing — when no id matches. */
export const DEFAULT_AGENT_ID = AGENTS[5].id;

export interface TrustItem {
  title: string;
  body: string;
}

export const TRUST: TrustItem[] = [
  {
    title: "Tenant isolation",
    body: "Corporate Memory lives in a sealed tenant only your organization can reach. Never shared, never co-mingled.",
  },
  {
    title: "Scoped agents",
    body: "Agents inherit the permissions of the role they serve. A Finance Agent cannot read HR records.",
  },
  {
    title: "Audit trail",
    body: "Every intent and agent action is logged with actor, timestamp, and outcome, exportable on demand.",
  },
  {
    title: "Encryption",
    body: "TLS in transit, encryption at rest, with per-tenant key management.",
  },
];

export interface FaqItem {
  q: string;
  a: string;
}

export const FAQS: FaqItem[] = [
  {
    q: "Do we have to replace our existing HR or accounting system?",
    a: "No. AI2Aim is designed to sit across the tools you already run. It works from the documents, records, and conversations those systems produce. Which of your systems can be connected directly depends on what you use, so it is worth walking through your stack on a demo.",
  },
  {
    q: "What does the AI actually do, in plain terms?",
    a: "You state what you need in plain language. AI2Aim resolves it against your data, hands it to the right agent, runs it, and logs the result with a full audit trail.",
  },
  {
    q: "Where does our data live, and who can see it?",
    a: "In a sealed tenant in aws-ca-central-1, encrypted at rest and in transit. Only your organization can reach it, and agents see only what the role they serve is permitted to see.",
  },
  {
    q: "Are you certified for SOC 2 or ISO?",
    a: "We run on enterprise-grade infrastructure with AES-256-GCM encryption, role-based access control, and full audit logging. For the current status of formal certifications, ask us directly and we will give you a straight answer.",
  },
  {
    q: "How long does it take to get running?",
    a: "Most teams connect their first process in days, not months. Bring one process to a demo and we will configure an agent for it live.",
  },
  {
    q: "What does it cost?",
    a: "Pricing scales with the modules and agents you run. Ask on a demo for a number specific to your stack. No generic tiers, no surprises.",
  },
];

/**
 * Asset paths. The video is the one canvas upload that had to be re-sourced.
 *
 * The mark ships as two masters and always has — index.html preloads both.
 * `ai2aim-logo.png` is the navy gradient for light grounds; its darkest stop
 * measures about 1.17:1 against the dark paper, so it cannot serve there.
 * `ai2aim-logo-dark.png` is the white artwork for dark grounds.
 *
 * The canvas shipped a single `dcv2-logo.png` on both themes, which meant the
 * navy mark sat on near-black in dark mode at that 1.17:1. Use `logoFor` and
 * the ground decides.
 */
export const LOGO_ON_LIGHT = "/brand/ai2aim-logo.png";
export const LOGO_ON_DARK = "/brand/ai2aim-logo-dark.png";

/**
 * Pick the master for the ground the mark sits on — not for the page theme.
 * The footer and the dashboard render are dark in both themes, so they pass
 * `true` regardless of what the page is doing.
 */
export function logoFor(onDarkGround: boolean): string {
  return onDarkGround ? LOGO_ON_DARK : LOGO_ON_LIGHT;
}
export const HERO_VIDEO_SRC = "/video/ai2aim-hero.mp4";
